import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RequireAuth, RequireGuest, RequireAdmin } from "@/components/RouteGuard";
import { CookieBanner } from "@/components/CookieBanner";

// Root-path redirect varies by hostname so admin.fortehealth.co lands on
// the admin sign-in page, while portal.fortehealth.co / *.pages.dev land
// on the patient dashboard. The same SPA bundle serves both hosts.
function RootRedirect() {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isAdminHost = host === "admin.fortehealth.co" || host.startsWith("admin.");
  return <Navigate to={isAdminHost ? "/admin/login" : "/dashboard"} replace />;
}

import Login from "@/routes/Login";
import Signup from "@/routes/Signup";
import ForgotPassword from "@/routes/ForgotPassword";
import ResetPassword from "@/routes/ResetPassword";
import ConfirmEmail from "@/routes/ConfirmEmail";
import Dashboard from "@/routes/Dashboard";
import MyTreatment from "@/routes/MyTreatment";
import Shots from "@/routes/Shots";
import Shop from "@/routes/Shop";
import Messages from "@/routes/Messages";
import Progress from "@/routes/Progress";
import Visits from "@/routes/Visits";
import Billing from "@/routes/Billing";
import Labs from "@/routes/Labs";
import LabDetail from "@/routes/LabDetail";
import Help from "@/routes/Help";
import HelpArticle from "@/routes/HelpArticle";
import AccountSettings from "@/routes/AccountSettings";
import PrivacyAndData from "@/routes/PrivacyAndData";
import AdminLogin from "@/routes/AdminLogin";
import AdminForgotPassword from "@/routes/AdminForgotPassword";
import AdminUsers from "@/routes/AdminUsers";
import AdminMessages from "@/routes/AdminMessages";
import AdminRefills from "@/routes/AdminRefills";
import AdminAnalytics from "@/routes/AdminAnalytics";
import AdminAdmins from "@/routes/AdminAdmins";
import AdminAuditLog from "@/routes/AdminAuditLog";
import AdminMfaSetup from "@/routes/AdminMfaSetup";
import AdminMfaChallenge from "@/routes/AdminMfaChallenge";
import NotFound from "@/routes/NotFound";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />

          <Route path="/auth/confirm" element={<ConfirmEmail />} />
          <Route path="/auth/reset" element={<ResetPassword />} />

          <Route element={<RequireGuest />}>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
          </Route>

          <Route element={<RequireAuth />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/treatment" element={<MyTreatment />} />
            <Route path="/shots" element={<Shots />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/progress" element={<Progress />} />
            <Route path="/visits" element={<Visits />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/labs" element={<Labs />} />
            <Route path="/labs/:panelId" element={<LabDetail />} />
            <Route path="/help" element={<Help />} />
            <Route path="/help/:id" element={<HelpArticle />} />
            <Route path="/account" element={<AccountSettings />} />
            <Route path="/account/privacy" element={<PrivacyAndData />} />
          </Route>

          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
          <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
          {/* MFA enrollment + challenge live outside RequireAdmin (which
              forces aal2) — they're the path to GET to aal2. They still
              require a signed-in admin-role session. */}
          <Route path="/admin/mfa/setup"     element={<AdminMfaSetup />} />
          <Route path="/admin/mfa/challenge" element={<AdminMfaChallenge />} />
          <Route element={<RequireAdmin />}>
            <Route path="/admin/users"     element={<AdminUsers />} />
            <Route path="/admin/messages"  element={<AdminMessages />} />
            <Route path="/admin/refills"   element={<AdminRefills />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/admins"    element={<AdminAdmins />} />
            <Route path="/admin/audit-log" element={<AdminAuditLog />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
        <CookieBanner />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
